import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import api, { money } from "@/lib/api";
import RoleShell from "@/components/RoleShell";
import { Stat, StatusBadge, VehicleBadge } from "@/components/shared";
import TrackingMap from "@/components/TrackingMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Navigation, Wallet, Package, Bike, MapPin, Truck } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

const TABS = [
  { key: "deliveries", label: "Deliveries", icon: Navigation },
  { key: "earnings", label: "Earnings", icon: Wallet },
  { key: "vehicle", label: "Vehicle", icon: Truck },
];
const VEHICLES = ["motorcycle", "car", "van", "truck"];

const COURIER_STEPS = {
  COURIER_ASSIGNED: ["arriving", "courier.headToStore"],
  COURIER_ARRIVING: ["pickup", "courier.confirmPickup"],
  PICKED_UP: ["transit", "courier.startDelivery"],
  IN_TRANSIT: ["deliver", "courier.confirmDelivery"],
};

export default function CourierApp() {
  const { user, checkAuth } = useAuth();
  const { t } = useTranslation();
  const [active, setActive] = useState("deliveries");
  const [me, setMe] = useState(user);
  const [available, setAvailable] = useState([]);
  const [mine, setMine] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [track, setTrack] = useState(null);
  const [gps, setGps] = useState("idle");
  const gpsRef = useRef({ lastAt: 0, lat: null, lng: null });

  const online = me?.courier?.availability && me.courier.availability !== "offline";

  const load = useCallback(async () => {
    const [m, av, o, e] = await Promise.all([
      api.get("/auth/me"), api.get("/courier/available").catch(() => ({ data: [] })),
      api.get("/orders"), api.get("/courier/earnings"),
    ]);
    setMe(m.data); setAvailable(av.data); setMine(o.data); setEarnings(e.data);
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, [load]);

  const activeDelivery = mine.find((o) => ["COURIER_ASSIGNED", "COURIER_ARRIVING", "PICKED_UP", "IN_TRANSIT"].includes(o.status));

  // server-side tracking (ETA, distance remaining, staleness) for the active delivery
  useEffect(() => {
    if (!activeDelivery) { setTrack(null); return; }
    const fetchTrack = () => api.get(`/orders/${activeDelivery.id}/tracking`).then((r) => setTrack(r.data)).catch(() => {});
    fetchTrack();
    const id = setInterval(fetchTrack, 6000);
    return () => clearInterval(id);
  }, [activeDelivery?.id, activeDelivery?.status]);

  // Secure, throttled location reporting: real GPS when granted, simulation fallback otherwise.
  useEffect(() => {
    if (!activeDelivery) { setGps("idle"); return; }
    const waypoint = ["PICKED_UP", "IN_TRANSIT"].includes(activeDelivery.status) ? activeDelivery.address : activeDelivery.store_location;
    let simTimer, webWatchId, nativeWatchId, cancelled = false;
    const havM = (a, b, c, d) => {
      const R = 6371000, r = (x) => (x * Math.PI) / 180;
      const dp = r(c - a), dl = r(d - b);
      const h = Math.sin(dp / 2) ** 2 + Math.cos(r(a)) * Math.cos(r(c)) * Math.sin(dl / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(h));
    };
    const send = async (lat, lng, accuracy) => {
      const nowMs = Date.now(), g = gpsRef.current;
      const moved = g.lat == null ? 9999 : havM(g.lat, g.lng, lat, lng);
      if (nowMs - g.lastAt < 8000 && moved < 25) return; // client throttle (scale-friendly)
      g.lastAt = nowMs; g.lat = lat; g.lng = lng;
      try { await api.post("/courier/location", accuracy != null ? { lat, lng, accuracy } : { lat, lng }); } catch {}
    };
    const startSim = () => {
      let cur = activeDelivery.courier_location || activeDelivery.store_location;
      const tick = () => {
        cur = { lat: cur.lat + (waypoint.lat - cur.lat) * 0.18 + (Math.random() - 0.5) * 0.0008,
                lng: cur.lng + (waypoint.lng - cur.lng) * 0.18 + (Math.random() - 0.5) * 0.0008 };
        send(cur.lat, cur.lng);
      };
      tick();
      simTimer = setInterval(tick, 5000);
    };
    const startWatch = async () => {
      // Native iOS/Android: use Capacitor Geolocation for reliable permission prompts + GPS.
      if (Capacitor.isNativePlatform()) {
        try {
          const perm = await Geolocation.requestPermissions();
          if (cancelled) return;
          if (perm.location !== "granted" && perm.coarseLocation !== "granted") {
            setGps("denied"); startSim(); return;
          }
          nativeWatchId = await Geolocation.watchPosition(
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 },
            (pos, err) => {
              if (cancelled) return;
              if (err || !pos) {
                setGps(err && (err.code === 1 || /denied/i.test(err.message || "")) ? "denied" : "unavailable");
                startSim(); return;
              }
              setGps("active"); send(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
            }
          );
          if (cancelled && nativeWatchId) Geolocation.clearWatch({ id: nativeWatchId });
        } catch { if (!cancelled) { setGps("unavailable"); startSim(); } }
        return;
      }
      // Website: keep the existing browser geolocation implementation.
      if (navigator.geolocation) {
        webWatchId = navigator.geolocation.watchPosition(
          (pos) => { if (cancelled) return; setGps("active"); send(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy); },
          (err) => { if (cancelled) return; setGps(err.code === 1 ? "denied" : "unavailable"); startSim(); },
          { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
        );
      } else { setGps("unavailable"); startSim(); }
    };
    startWatch();
    return () => {
      cancelled = true;
      if (webWatchId != null) navigator.geolocation.clearWatch(webWatchId);
      if (nativeWatchId) Geolocation.clearWatch({ id: nativeWatchId });
      if (simTimer) clearInterval(simTimer);
    };
  }, [activeDelivery?.id, activeDelivery?.status]);

  const toggleOnline = async (v) => {
    await api.post("/courier/availability", { availability: v ? "online" : "offline" });
    await load();
  };
  const accept = async (id) => { try { await api.post(`/courier/orders/${id}/accept`); toast.success(t("courier.accepted")); load(); } catch (e) { toast.error(e.response?.data?.detail || t("common.actionFailed")); } };
  const step = async (id, action) => { await api.post(`/courier/orders/${id}/${action}`); toast.success(t("courier.updated")); load(); };

  return (
    <RoleShell title={t("roles.courier")} tabs={TABS} active={active} setActive={setActive}
      right={<div className="flex items-center gap-2 text-sm"><span className={online ? "text-emerald-600 font-medium" : "text-muted-foreground"}>{online ? t("courier.online") : t("courier.offline")}</span><Switch data-testid="online-toggle" checked={!!online} onCheckedChange={toggleOnline} /></div>}>

      {active === "deliveries" && (
        <div className="space-y-6">
          {!me?.courier?.vehicle_type && <div className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 p-4 text-sm">{t("courier.setVehicleWarn")}</div>}

          {activeDelivery && (
            <div className="rounded-2xl border bg-card overflow-hidden">
              <div className="px-5 py-3 border-b bg-muted/40 flex items-center justify-between">
                <span className="font-semibold flex items-center gap-2"><Navigation className="w-4 h-4 text-brand" /> {t("courier.activeDelivery")}</span>
                <StatusBadge status={activeDelivery.status} />
              </div>
              <div className="p-5 space-y-4">
                <TrackingMap pickup={track?.pickup || activeDelivery.store_location} destination={track?.destination || activeDelivery.address} courier={track?.courier || activeDelivery.courier_location} phase={track?.phase} active etaMinutes={track?.eta_minutes} online={track?.courier_online} stale={track?.courier_stale} lastUpdated={track?.last_updated} height={240} />
                {gps !== "idle" && gps !== "active" && (
                  <div data-testid="gps-banner" className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2">{gps === "denied" ? t("track.gpsDenied") : t("track.gpsUnavailable")}</div>
                )}
                {gps === "active" && <div data-testid="gps-banner" className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs px-3 py-2">{t("track.gpsActive")}</div>}
                <div className="text-sm space-y-1">
                  <div className="flex items-center gap-2"><Package className="w-4 h-4 text-muted-foreground" /> {activeDelivery.store_name} → {activeDelivery.customer_name}</div>
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /> {activeDelivery.address?.line1}, {activeDelivery.address?.city} · {activeDelivery.distance_km}km</div>
                  <div className="text-brand font-semibold">{t("courier.earning")}: {money(activeDelivery.courier_earnings, activeDelivery.currency)}</div>
                </div>
                {COURIER_STEPS[activeDelivery.status] && (
                  <Button data-testid="courier-step-btn" onClick={() => step(activeDelivery.id, COURIER_STEPS[activeDelivery.status][0])} className="w-full h-11 rounded-xl bg-brand hover:bg-brand-dark">
                    {t(COURIER_STEPS[activeDelivery.status][1])}
                  </Button>
                )}
              </div>
            </div>
          )}

          <div>
            <div className="font-semibold mb-3">{t("courier.availableRequests")} {online ? "" : t("courier.goOnlineHint")}</div>
            {available.length === 0 ? <div className="text-sm text-muted-foreground rounded-2xl border bg-card p-6 text-center">{t("courier.noRequests")}</div> : (
              <div className="space-y-3">
                {available.map((o) => (
                  <div key={o.id} data-testid={`avail-${o.id}`} className="rounded-2xl border bg-card p-4 flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-semibold">{o.store_name} → {o.address?.city}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{o.metrics.total_weight_kg}kg · {o.metrics.total_packages} · {o.distance_km}km</div>
                      <div className="flex gap-1.5 mt-2"><VehicleBadge type={o.recommended_vehicle} active /></div>
                    </div>
                    <div className="text-right">
                      <div className="text-brand font-display font-bold">{money(o.courier_earnings, o.currency)}</div>
                      <Button data-testid={`accept-${o.id}`} size="sm" disabled={!!activeDelivery} onClick={() => accept(o.id)} className="mt-2 rounded-full bg-brand hover:bg-brand-dark">{t("courier.accept")}</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {active === "earnings" && earnings && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Stat label={t("courier.totalEarnings")} value={money(earnings.total)} icon={Wallet} accent="text-emerald-500" />
            <Stat label={t("courier.deliveries")} value={earnings.deliveries} icon={Package} />
          </div>
          <div className="rounded-2xl border bg-card p-5">
            <div className="font-semibold mb-3">{t("courier.history")}</div>
            {earnings.records.length === 0 ? <div className="text-sm text-muted-foreground">{t("courier.noDeliveries")}</div> :
              earnings.records.map((r) => (
                <div key={r.id} className="flex justify-between py-2 border-b last:border-0 text-sm">
                  <span className="font-mono text-xs text-muted-foreground">#{r.order_id.slice(-8)}</span>
                  <span className="font-semibold text-emerald-600">+{money(r.amount)}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {active === "vehicle" && <VehicleForm me={me} onSaved={() => { load(); checkAuth(); }} />}
    </RoleShell>
  );
}

function VehicleForm({ me, onSaved }) {
  const { t } = useTranslation();
  const [type, setType] = useState(me?.courier?.vehicle_type || "");
  const [plate, setPlate] = useState(me?.courier?.vehicle_info?.plate || "");
  const [model, setModel] = useState(me?.courier?.vehicle_info?.model || "");
  const save = async () => {
    if (!type) { toast.error(t("courier.selectVehicle")); return; }
    await api.post("/courier/vehicle", { vehicle_type: type, plate, model });
    toast.success(t("courier.vehicleSaved")); onSaved();
  };
  return (
    <div className="max-w-lg rounded-2xl border bg-card p-5 space-y-5">
      <div>
        <Label className="mb-2 block">{t("courier.vehicleType")}</Label>
        <div className="grid grid-cols-2 gap-3">
          {VEHICLES.map((v) => (
            <button key={v} data-testid={`veh-${v}`} onClick={() => setType(v)}
              className={`rounded-xl border p-4 text-start ${type === v ? "border-brand bg-accent" : "hover:border-brand/40"}`}>
              <Bike className="w-5 h-5 text-brand mb-2" /><div className="font-semibold">{t(`vehicle.${v}`)}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>{t("form.plate")}</Label><Input data-testid="veh-plate" value={plate} onChange={(e) => setPlate(e.target.value)} className="mt-1 rounded-xl" /></div>
        <div><Label>{t("form.model")}</Label><Input data-testid="veh-model" value={model} onChange={(e) => setModel(e.target.value)} className="mt-1 rounded-xl" /></div>
      </div>
      <div className="text-xs text-muted-foreground">{t("courier.verification")}: {me?.courier?.verification || "pending"}</div>
      <Button data-testid="save-vehicle" onClick={save} className="rounded-full bg-brand hover:bg-brand-dark">{t("courier.saveVehicle")}</Button>
    </div>
  );
}
